import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import QuickItemViewBox from "../../components/DataDisplayer/QuickItemViewBox";
import BaseApp, { IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import LocationsApp from "../locations/LocationsApp";
import { addNewLocation, useReturnAllLocations } from "../locations/locationsSlice";
import { addNewWarehouse, deleteWarehouse, setSelectedWarehouses, updateAllWarehouses, updateWarehouse, updateWarehousesLoadingStatus, updateWarehousesPagination, updateWarehousesSearch, updateWarehousesSearchParams, useReturnAllWarehouses, useReturnWarehousesLoadingStatus, useReturnWarehousesPagination, useReturnWarehousesSearch, useReturnWarehousesSearchParams, useReturSelectedWarehouses } from "./warehousesSlice";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { Link } from "react-router-dom";
import { ICellRendererParams } from "ag-grid-community";
import { JSX } from "react/jsx-runtime";
import ViewSingleWarehousePage from "./ViewSingleWarehousePage";
import api from "../../services/api";

/**
 * Warehouse Model
 */
export interface IWarehouseModel {
    id: number,
    title: string,
    thumbnail: string,
    created_by: string,
    location_id: number,
    date_created: string,
    last_updated: string,
    is_active: boolean,
    location_title: string,
    location_country: string,
    location_address: string
}

export const EmptyWarehouseModel: IWarehouseModel = {
    id: 0,
    title: "",
    thumbnail: "",
    created_by: "",
    location_id: 0,
    date_created: "",
    last_updated: "",
    is_active: false,
    location_title: "",
    location_country: "",
    location_address: ""
}

/**
 * Warehouses App
 */
export default class WarehousesApp extends BaseApp{

    appName = "warehouses"
    appBaseEndPoint = "/warehouses"

    elemPrefix: string = "warehouses_"

    messages = {
        create: "Create a new warehouse",
        createShort: "New warehouse",
        edit: "Edit warehouse",
        editShort: "Edit",
        delete: "Delete warehouse",
        deleteShort: "Delete",
        view: "View warehouse",
        nothingFound: "No warehouses have been found.",
    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title"
            },
            {
                id: "location_title",
                type: "text",
                label: "Location",
                quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllLocations} itemId={item.location_id} baseApp={new LocationsApp()} />
            },
            {
                id: "location_country",
                type: "country",
                label: "Country"
            },
            {
                id: "location_address",
                type: "text",
                label: "Address"
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No warehouses have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "warehouses_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 8,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "location_id",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Location",
                    fullWidth: true,
                    appClass: new LocationsApp(),
                    selectLabel: "Select Location",
                    gridColumns: new LocationsApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Title, location, etc."
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
            {
                id: this.elemPrefix + "location_id",
                message: "Location is required!"
            }
        ]
    }

    quickPreview = (id: number) => {
        return <ViewSingleWarehousePage customId={id} />
    }

    /**
     * Dynamic base page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllWarehouses,
        pagination: useReturnWarehousesPagination,
        loading: useReturnWarehousesLoadingStatus,
        search: useReturnWarehousesSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateWarehousesLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteWarehouse({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewWarehouse((item as IWarehouseModel))),
        updateItem: (item, dispatch) => dispatch(updateWarehouse((item as IWarehouseModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllWarehouses((items as IWarehouseModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateWarehousesSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateWarehousesPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/warehouses/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/warehouses/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
    }

    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "thumbnail",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "title",
        //     headerName: "Title",
        //     cellRenderer: (params: ICellRendererParams<IWarehouseModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/warehouses/view/${item.id}`}>
        //                     <h3 className="title">{item.title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "title",
            headerName: "Warehouse",
            type: "title",
            width: 300,
            minWidth: 300,
            cellRendererParams: {
                appClass: () => new WarehousesApp(),
                enableQuickPreview: true,
            }
        },
        // {
        //     field: "location_title",
        //     headerName: "Location",
        //     cellRenderer: (params: ICellRendererParams<IWarehouseModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/locations/view/${item.location_id}`}>
        //                     <h3 className="title">{item.location_title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "location_title",
            headerName: "Location",
            type: "quick-preview",
            width: 300,
            minWidth: 300,
            cellRendererParams: {
                appClass: () => new LocationsApp(),
                elemIdTitle: "location_id"
            }
        },
        {
            field: "location_country",
            headerName: "Country",
            type: "country",
        },
        {
            field: "location_address",
            headerName: "Address",
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/warehouses/view/[id]",
        editItemLink: "/panel/warehouses/edit/[id]",
        filterForm: {
            name: "warehouses-data-displayer-filter-form",
            elements: [
                // {
                //     id: "location_id",
                //     xs: 12,
                //     type: "SelectLocation",
                //     label: "Location",
                //     fullWidth: true,
                //     addNoneItem: true,
                // },
                {
                    id: "location_id",
                    xs: 12,
                    type: "DynamicModelSelect",
                    label: "Select Location",
                    fullWidth: true,
                    appClass: new LocationsApp(),
                    selectLabel: "Select Location",
                    gridColumns: new LocationsApp().gridDataColumns
                },
            ]
        }
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturSelectedWarehouses,
        searchParams: useReturnWarehousesSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedWarehouses(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateWarehousesSearchParams(params))
    }

    /**
     * Warehouses locations list
     */
    returnWarehousesLocationsList = async() => {
        return await api.get(this.appBaseEndPoint + "/warehouses-locations")
    }

}